import { type GqlExecutor } from '@hopdrive/sdk-core'
import type { BuildEventInput, EventlogInsertInput } from '../../types'
import type { EventKey } from '../../types/event-keys'
import { EventValidationError } from '../../errors/event-errors'
import { getEventKeyDefinition } from '../../registry'
import { getActionByName } from '../lookup-types/getActionByName.js'
import { getRoleByName } from '../lookup-types/getRoleByName.js'

export async function buildEvent(
  options: BuildEventInput,
  executor?: GqlExecutor
): Promise<EventlogInsertInput> {
  const { key, user, role, metadata, customer_id, move_id, lane_id, location_id, driver_id } = options

  if (!key || typeof key !== 'string') {
    throw new EventValidationError('key', 'Key is required and must be a string')
  }
  if (!user || typeof user !== 'string') {
    throw new EventValidationError('user', 'User is required and must be a string')
  }
  if (!role || typeof role !== 'string') {
    throw new EventValidationError('role', 'Role is required and must be a string')
  }
  if (metadata !== undefined && typeof metadata !== 'object') {
    throw new EventValidationError('metadata', 'Metadata must be an object')
  }
  if (customer_id !== undefined && typeof customer_id !== 'number') {
    throw new EventValidationError('customer_id', 'customer_id must be a number')
  }
  if (move_id !== undefined && typeof move_id !== 'number') {
    throw new EventValidationError('move_id', 'move_id must be a number')
  }
  if (lane_id !== undefined && typeof lane_id !== 'number') {
    throw new EventValidationError('lane_id', 'lane_id must be a number')
  }
  if (location_id !== undefined && typeof location_id !== 'number') {
    throw new EventValidationError('location_id', 'location_id must be a number')
  }
  if (driver_id !== undefined && typeof driver_id !== 'number') {
    throw new EventValidationError('driver_id', 'driver_id must be a number')
  }

  if (!options.skipMetadataValidation) {
    const def = getEventKeyDefinition(key as EventKey)
    if (def?.metadataSchema && metadata !== undefined) {
      const result = def.metadataSchema.safeParse(metadata)
      if (!result.success) {
        throw new EventValidationError('metadata', `Metadata does not match schema for "${key}": ${result.error.message}`)
      }
    }
  }

  const actionType = await getActionByName(key, executor)
  const roleType = await getRoleByName(role, executor)

  const eventlog: EventlogInsertInput = {
    event_time_utc: new Date().toISOString(),
    actiontype_id: actionType.id,
    user,
    role_id: roleType.id,
    metadata,
    customer_id,
    move_id,
    lane_id,
    location_id,
    driver_id,
  }

  return eventlog
}
